import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const defaultState = {
  name: "",
  company: "",
  phone: "",
  email: "",
  package: "year",
  message: "",
};

export default function ContactForm({ defaultPackage = "year" }) {
  const [form, setForm] = useState({ ...defaultState, package: defaultPackage });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.phone || !form.email) {
      toast.error("Täytäthän nimi, yritys, puhelin ja sähköposti.");
      return;
    }
    if (!consent) {
      toast.error("Hyväksy tietosuojaseloste jatkaaksesi.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Kiitos! Olemme sinuun yhteydessä pian.");
      setForm({ ...defaultState, package: defaultPackage });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Lähetys epäonnistui. Yritä uudelleen.";
      toast.error(typeof msg === "string" ? msg : "Lähetys epäonnistui.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-success"
        className="rounded-2xl border border-[#22D3EE]/40 bg-[#0D1B2A] p-10 text-center led-glow"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-[#22D3EE]" />
        <h3 className="mt-4 font-display text-2xl font-semibold">Kiitos yhteydenotostasi!</h3>
        <p className="mt-2 text-neutral-400">
          Otamme sinuun yhteyttä mahdollisimman pian sopivan mainospaikan löytämiseksi.
        </p>
        <button
          data-testid="contact-new-btn"
          onClick={() => setDone(false)}
          className="mt-6 inline-flex items-center gap-2 text-[#22D3EE] hover:text-white transition-colors"
        >
          Lähetä uusi viesti <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="rounded-2xl border border-white/10 bg-[#0D1B2A] p-6 md:p-8 backdrop-blur"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="text-neutral-300">Nimi *</Label>
          <Input
            id="name"
            data-testid="contact-name"
            value={form.name}
            onChange={update("name")}
            placeholder="Etunimi Sukunimi"
            className="mt-2 bg-[#0A1729] border-[#1E2F44] text-white placeholder:text-neutral-600 focus-visible:ring-[#22D3EE]"
          />
        </div>
        <div>
          <Label htmlFor="company" className="text-neutral-300">Yrityksen nimi *</Label>
          <Input
            id="company"
            data-testid="contact-company"
            value={form.company}
            onChange={update("company")}
            placeholder="Esim. Ravintola Esimerkki Oy"
            className="mt-2 bg-[#0A1729] border-[#1E2F44] text-white placeholder:text-neutral-600 focus-visible:ring-[#22D3EE]"
          />
        </div>
        <div>
          <Label htmlFor="phone" className="text-neutral-300">Puhelin *</Label>
          <Input
            id="phone"
            data-testid="contact-phone"
            value={form.phone}
            onChange={update("phone")}
            placeholder="+358 40 123 4567"
            className="mt-2 bg-[#0A1729] border-[#1E2F44] text-white placeholder:text-neutral-600 focus-visible:ring-[#22D3EE]"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-neutral-300">Sähköposti *</Label>
          <Input
            id="email"
            data-testid="contact-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="nimi@yritys.fi"
            className="mt-2 bg-[#0A1729] border-[#1E2F44] text-white placeholder:text-neutral-600 focus-visible:ring-[#22D3EE]"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-neutral-300">Kiinnostava mainosjakso</Label>
          <Select value={form.package} onValueChange={update("package")}>
            <SelectTrigger
              data-testid="contact-package"
              className="mt-2 bg-[#0A1729] border-[#1E2F44] text-white focus:ring-[#22D3EE]"
            >
              <SelectValue placeholder="Valitse jakso" />
            </SelectTrigger>
            <SelectContent className="bg-[#0D1B2A] border-[#1E2F44] text-white">
              <SelectItem value="week" data-testid="pkg-week">1 viikko – 400 € + alv</SelectItem>
              <SelectItem value="month" data-testid="pkg-month">1 kuukausi – 1000 € + alv</SelectItem>
              <SelectItem value="year" data-testid="pkg-year">12 kuukautta – 6000 € + alv (Suosituin)</SelectItem>
              <SelectItem value="unspecified" data-testid="pkg-any">En osaa vielä sanoa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="message" className="text-neutral-300">Viesti</Label>
          <Textarea
            id="message"
            data-testid="contact-message"
            value={form.message}
            onChange={update("message")}
            rows={4}
            placeholder="Kerro lyhyesti yrityksestäsi ja millaista näkyvyyttä haet."
            className="mt-2 bg-[#0A1729] border-[#1E2F44] text-white placeholder:text-neutral-600 focus-visible:ring-[#22D3EE]"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="mt-6 group inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#22D3EE] px-6 py-4 font-bold text-black transition-all hover:bg-[#67E8F9] led-glow disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Lähetetään…
          </>
        ) : (
          <>
            Varaa mainospaikka
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </>
        )}
      </button>

      <label className="mt-4 flex items-start gap-2 text-xs text-neutral-400 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="contact-consent"
          className="mt-0.5 h-4 w-4 rounded border-[#1E2F44] bg-[#0A1729] accent-[#22D3EE]"
        />
        <span>
          Hyväksyn yhteystietojeni käsittelyn yhteydenottoa varten. Lue{" "}
          <Link to="/tietosuoja" className="text-[#22D3EE] hover:text-[#67E8F9] underline">
            tietosuojaseloste
          </Link>
          .
        </span>
      </label>

      <p className="mt-3 text-center text-xs text-neutral-500">
        Vastaamme yleensä saman arkipäivän aikana. Ei sitoumuksia.
      </p>
    </form>
  );
}
